import React from 'react';
import Modal, { ModalSize } from '@shared/ui/Modal';

interface AdminModalProps {
  isOpen: boolean;
  title: string;
  /** Optional subheading rendered under the title */
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional footer (action buttons); rendered right-aligned */
  footer?: React.ReactNode;
  size?: ModalSize;
}

/**
 * Kept as a name, not as an implementation: this is now a thin pass-through to
 * the shared <Modal>. It used to be a second dialog primitive — its own portal,
 * its own Escape handler, its own markup — built before the shared one existed.
 *
 * Collapsing it means the 25 admin pages that mount it pick up, with no call-site
 * change, the three things it never had: a focus trap (Tab used to walk out of
 * the dialog into the page behind the scrim), a body scroll lock, and unique
 * heading ids — it hardcoded aria-labelledby="admin-modal-title" and
 * aria-describedby="admin-modal-subtitle", so two dialogs open at once pointed
 * screen readers at the same elements.
 *
 * `subtitle` maps to Modal's `description`, which is the same thing under the
 * name the shared component uses.
 *
 * New admin code can use <Modal>, <FormModal> or <HelpModal> directly. This
 * exists so 25 files did not have to change in one commit.
 */
const AdminModal: React.FC<AdminModalProps> = ({
  isOpen,
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'md',
}) => (
  <Modal
    isOpen={isOpen}
    title={title}
    description={subtitle}
    onClose={onClose}
    footer={footer}
    size={size}
  >
    {children}
  </Modal>
);

export default AdminModal;
